import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Geometry, LineString, Point } from "ol/geom";
import { Circle, Fill, RegularShape, Stroke, Style } from "ol/style";
import {
  RouteGeometry,
  VoyageRoute,
  VoyageRoutingModel,
} from "../models/voyage-routing-model";
import { Feature } from "ol";
import { CreateDateLineAndGreatCircleFeatures } from "../utils/map-utils";
import { isGreatCircleLeg, offsetLineCoordinates } from "../utils/map-utils";
import * as turf from "@turf/turf";
import { transform } from "ol/proj";
import { Coordinate } from "ol/coordinate";
import { PlanningLayersColors } from "./types/plan-layer.types";
import { SuggestedRouteEvents } from "../../local_modules/voyage-suggested-routes/src/types";

const _PROJECTION = (<any>window)["appenv"].projection;
const nmToKm = 1.852;

export class BufferLayers extends EventTarget {
  constructor(colors?: Partial<PlanningLayersColors>) {
    super();
    this.setColors(colors || {});
    this._onRoutesChanged = this._onRoutesChanged.bind(this);
  }

  get colors(): PlanningLayersColors {
    return this._colors;
  }

  get layers(): VectorLayer<VectorSource<Geometry>>[] {
    return this._layers;
  }

  async initialize(): Promise<void> {
    this._routesBuffersVectorSource = new VectorSource();

    this._layers.push(
      new VectorLayer({
        source: this._routesBuffersVectorSource,
        visible: true,
      })
    );

    VoyageRoutingModel!.addEventListener(
      SuggestedRouteEvents.RoutesChanged,
      this._onRoutesChanged
    );

    VoyageRoutingModel!.addEventListener(
      "route-view-status-changed",
      this._onRoutesChanged
    );
  }

  setColors(colors: Partial<PlanningLayersColors>): void {
    this.colors.defaultColor = colors.defaultColor || this.colors.defaultColor;
    this.colors.uiRouteColors =
      colors.uiRouteColors || this.colors.uiRouteColors;
    this.colors.warningColor = colors.warningColor || this.colors.warningColor;
    this.colors.avoidColor = colors.avoidColor || this.colors.avoidColor;
    this.colors.parametricRollColor =
      colors.parametricRollColor || this.colors.parametricRollColor;
    this.colors.waypointFillColor =
      colors.waypointFillColor || this.colors.waypointFillColor;
    this.colors.passedColor = colors.passedColor || this.colors.passedColor;
    this.colors.heavyRollingColor =
      colors.heavyRollingColor || this.colors.heavyRollingColor;
    this.colors.transverseAccelerationColor =
      colors.transverseAccelerationColor || this.colors.transverseAccelerationColor;

    if (this._routesBuffersVectorSource) {
      this._onRoutesChanged();
    }
  }

  clearRoutes() {
    try {
      this._routesBuffersVectorSource!.clear();
    } catch (error) {
      console.warn(error);
    }
  }

  private _colors: PlanningLayersColors = {
    defaultColor: "black",
    uiRouteColors: [],
    warningColor: "yellow",
    avoidColor: "red",
    waypointFillColor: "black",
    parametricRollColor: "#FE92E9",
    heavyRollingColor: "#8454D6",
    transverseAccelerationColor: "#BF6410",
    passedColor: "grey",
  };

  private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
  private _routesBuffersVectorSource: VectorSource<Geometry> | null = null;

  private _onRoutesChanged() {
    this.clearRoutes();
    const uiRoutes = VoyageRoutingModel!.uiRoutes;
    const hiddenItems = VoyageRoutingModel!._getHiddenRoutes()
    
    VoyageRoutingModel!.routes.forEach((route) => {
      if((route.routeCreatedBy === "bridge" || route.routeCreatedBy === "system") && !hiddenItems.includes(route.routeId)) {
      this._plotBuffers(
        route,
        uiRoutes.findIndex((uiRoute) => uiRoute.route === route)
      )};
    });
  }

  private _extractLineStrings(route_geometry: RouteGeometry) {
    const features: Feature<Geometry>[] = [];

    for (let idx = 0; idx < route_geometry.length; idx++) {
      const f = route_geometry[idx];
      if (f.geometry.type !== "LineString") continue;

      const lineString = new Feature({
        geometry: new LineString(f.geometry.coordinates),
      });

      if (f.properties.leg_type) {
        lineString.setProperties({ leg_type: f.properties.leg_type });
      }

      // Resolve max_x_track: prefer LineString's own value, fall back to preceding waypoint
      let xtd = f.properties.max_x_track;
      if (xtd == null && idx > 0) {
        const prevWp = route_geometry[idx - 1];
        if (prevWp.geometry.type === "Point") {
          xtd = prevWp.properties.max_x_track ?? prevWp.properties.userInput?.max_x_track;
        }
      }

      const feature = CreateDateLineAndGreatCircleFeatures(lineString);
      feature.forEach((feat) => {
        feat.setProperties({
          max_x_track: xtd ?? 2,
        });
      });

      features.push(...feature);
    }

    return features;
  }

  private _adjustCoordinatesForMeridianCrossing(coordinates: turf.helpers.Position[]) {
    for (let i = 1; i < coordinates.length - 1; i++) {
      const current = coordinates[i];
      const previous = coordinates[i - 1];
      const longitudeDifference = current[0] - previous[0];
      if (longitudeDifference > 180) {
        current[0] -= 360;
      } else if (longitudeDifference < -180) {
        current[0] += 360;
      }
    }
    return coordinates;
  }

  // Returns two parallel offset lines (port and starboard) per segment
  private _createOffsetLines(features: Feature<Geometry>[], nmToKm: number): turf.helpers.Feature<turf.helpers.LineString>[] {
    const offsetLines: turf.helpers.Feature<turf.helpers.LineString>[] = [];

    features.forEach((line) => {
      let max_x_track = line.getProperties().max_x_track;
      // Skip buffer if max_x_track is 0
      if (max_x_track === 0) return;
      // Cap max_x_track at 2nm
      if (max_x_track > 2) {
        max_x_track = 2;
      }

      const convertToKm = max_x_track * nmToKm;
      if (convertToKm <= 0) return;

      const coordinates = (<LineString>line.getGeometry()).getCoordinates();
      const greatCircle = isGreatCircleLeg(line.getProperties().leg_type);
      const starboardLine = turf.lineString(offsetLineCoordinates(coordinates, convertToKm, 1, greatCircle));
      const portLine = turf.lineString(offsetLineCoordinates(coordinates, convertToKm, -1, greatCircle));

      this._adjustCoordinatesForMeridianCrossing(starboardLine.geometry.coordinates);
      this._adjustCoordinatesForMeridianCrossing(portLine.geometry.coordinates);

      offsetLines.push(starboardLine, portLine);
    });

    return offsetLines;
  }

  private _createStyledLineFeature(coordinates: Coordinate[], uiRouteColor: string): Feature {
    const lineFeature = new Feature(new LineString(coordinates));
    lineFeature.setStyle(
      new Style({
        stroke: new Stroke({
          color: uiRouteColor,
          lineDash: [6, 4],
          width: 2,
        }),
      })
    );
    return lineFeature;
  }


  private _transformCoordinates(coordsArray: any[], projection: string): Coordinate[] {
    return coordsArray
      .map(innerArray => {
        if (Array.isArray(innerArray) && innerArray.length === 2) {
          const coordinate: Coordinate = [innerArray[0], innerArray[1]];
          return transform(coordinate, "EPSG:4326", projection);
        }
        return undefined;
      })
      .filter((coord): coord is Coordinate => coord !== undefined);
  }

  private _createXMarkFeature(lonLat: [number, number], color: string): Feature {
    const coord = transform(lonLat, "EPSG:4326", _PROJECTION);
    const feature = new Feature(new Point(coord));
    feature.setStyle(new Style({
      image: new RegularShape({
        points: 4,
        radius: 6,
        radius2: 0,
        angle: Math.PI / 4,
        stroke: new Stroke({ color, width: 2 }),
      }),
    }));
    return feature;
  }

  private _createDepthMarkFeature(lonLat: [number, number], color: string): Feature {
    const coord = transform(lonLat, "EPSG:4326", _PROJECTION);
    const feature = new Feature(new Point(coord));
    feature.setStyle(new Style({
      image: new Circle({
        radius: 5,
        stroke: new Stroke({ color, width: 2 }),
        fill: new Fill({ color: "transparent" }),
      }),
    }));
    return feature;
  }

  private _plotBuffers(route: VoyageRoute, uiRouteIndex: number) {
    if (!route.route_geometry?.length) {
      return;
    }

    const features = this._extractLineStrings(route.route_geometry);
    const offsetLines = this._createOffsetLines(features, nmToKm);

    let uiRouteColor = this.colors.defaultColor || "black";
    if (uiRouteIndex >= 0 && this.colors.uiRouteColors[uiRouteIndex]) {
      uiRouteColor = this.colors.uiRouteColors[uiRouteIndex];
    }

    // Offset lines come in pairs: [starboard0, port0, starboard1, port1, ...]
    for (let i = 0; i < offsetLines.length; i += 2) {
      const starboardCoords = this._transformCoordinates(offsetLines[i].geometry.coordinates, _PROJECTION);
      const portCoords = this._transformCoordinates(offsetLines[i + 1].geometry.coordinates, _PROJECTION);

      // Draw the two parallel dashed lines
      this._routesBuffersVectorSource?.addFeature(this._createStyledLineFeature(starboardCoords, uiRouteColor));
      this._routesBuffersVectorSource?.addFeature(this._createStyledLineFeature(portCoords, uiRouteColor));

      // Join parallel lines with straight lines at both ends of the segment
      if (starboardCoords.length > 0 && portCoords.length > 0) {
        this._routesBuffersVectorSource?.addFeature(
          this._createStyledLineFeature([starboardCoords[0], portCoords[0]], uiRouteColor)
        );
        this._routesBuffersVectorSource?.addFeature(
          this._createStyledLineFeature(
            [starboardCoords[starboardCoords.length - 1], portCoords[portCoords.length - 1]],
            uiRouteColor
          )
        );
      }
    }

    // Draw x_mark (×) and depth_mark (○) for each leg
    for (const f of route.route_geometry) {
      if (f.geometry.type !== "LineString") continue;
      const { x_mark, depth_mark } = f.properties;
      if (Array.isArray(x_mark) && x_mark.length === 2) {
        this._routesBuffersVectorSource?.addFeature(
          this._createXMarkFeature(x_mark as [number, number], uiRouteColor)
        );
      }
      if (Array.isArray(depth_mark) && depth_mark.length === 2) {
        this._routesBuffersVectorSource?.addFeature(
          this._createDepthMarkFeature(depth_mark as [number, number], uiRouteColor)
        );
      }
    }
  }
}
